import { RootStateCarteira } from '../../store/carteiraReducer';
import * as S from './styles';
import { connectUtil, PropsFromRedux } from 'base/src/utils/reduxUtil';
import { PageBanner } from 'base/src/components/PageBanner';
import Avatar from 'base/src/components/Avatar';
import { useTheme } from 'base/src/utils/context/themeContext';
import StringUtil from 'domain/utils/StringUtils';
import { Icon } from '@iconify/react';
import { formatToBRL } from 'base/src/utils/mathUtil';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { GetPagamentoDetalhe, GetPagamentoDetalheByExtratoID } from '../../store/pagamento/actions/pagamentoAction';
import { PedidosPagosResponse } from '../../types/detalheResgate/pedidosPagos';
import { DetalhePagamentoResponse } from '../../types/detalheResgate/detalhePagamentoResponse';
import { PagosResponse } from '../../types/detalheResgate/pagos';
import { formatDate } from 'base/src/utils/stringUtil';

const connector = connectUtil(
  (state: RootStateCarteira) => ({
    DetalheResgate: state.PagamentoReducer.PagamentoDetalhe,
    CarteiraUsuario: state.CarteiraBaseReducer.CarteiraUsuario,
  }),
  { GetPagamentoDetalhe, GetPagamentoDetalheByExtratoID }
);

export interface DetalhePagamentoComponentProps extends PropsFromRedux<typeof connector> { }

interface GroupedData {
  avatar: string;
  usuarioNome: string;
  pedidos: (PedidosPagosResponse & { status: string })[];
  pagos: PagosResponse[];
}

function DetalhePagamento(props: DetalhePagamentoComponentProps) {
  const { theme } = useTheme();
  const { id } = useParams();

  useEffect(function fetchDetalhe() {
    if (!props.CarteiraUsuario || props.CarteiraUsuario.length === 0) return;

    const carteiraId = props.CarteiraUsuario[0].CarteiraID;
    const num: number = Number(id);

    if (isNaN(num)) {
      props.GetPagamentoDetalhe(
        { CarteiraID: carteiraId, DataOperacao: id as string, ExtratoID: 0 },
        carteiraId,
        function onComplete() { }
      );
    } else {
      props.GetPagamentoDetalheByExtratoID({
        ExtratoID: num,
        CarteiraID: carteiraId,
        DataOperacao: ''
      }, carteiraId, function onComplete() { });
    }
  }, []);

  const groupedData = useMemo(function computeGroupedData() {
    return props.DetalheResgate.flatMap(function flatMapPagos(resgate: DetalhePagamentoResponse) {
      return resgate.Pagos ?? [];
    }).reduce<Record<string, GroupedData>>(function reduceByUser(acc, pagosItem: PagosResponse) {
      const key = `${pagosItem.Avatar}-${pagosItem.UsuarioNome}`;

      if (!acc[key]) {
        acc[key] = {
          avatar: pagosItem.Avatar,
          usuarioNome: pagosItem.UsuarioNome,
          pedidos: [],
          pagos: [],
        };
      }

      acc[key].pagos.push(pagosItem);

      (pagosItem.PedidosPagos ?? []).forEach(function pushPedido(pedidoItem: PedidosPagosResponse) {
        acc[key].pedidos.push({
          ...pedidoItem,
          status: pagosItem.Status,
        });
      });

      return acc;
    }, {});
  }, [props.DetalheResgate]);

  const totalValorTransacao = useMemo(function computeTotalValor() {
    return props.DetalheResgate.reduce(function sumValor(total, item) {
      return total + Number(item.ValorTransacao);
    }, 0);
  }, [props.DetalheResgate]);

  const totalQuantidadePremiacoesPagas = useMemo(function computeTotalPremiacoes() {
    return props.DetalheResgate.reduce(function sumPremiacoes(total, item) {
      return total + Number(item.QuantidadePremiacoesPagas);
    }, 0);
  }, [props.DetalheResgate]);

  function renderStatusTag(status: string) {
    const label = status === "DONE" ? "Confirmado" : status === "PENDING" ? "Pendente" : "Cancelado";
    const color = status === "DONE" ? theme.colors.positive : status === "PENDING" ? theme.colors.warning : theme.colors.negative;
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '5px 14px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        backgroundColor: `${color}14`,
        color: color,
      }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color }} />
        {label}
      </span>
    );
  }

  return (
    <S.Container>
      <PageBanner title="Detalhe Pagamento" subtile="Visualize todos os detalhes desta transação" />
      {props.DetalheResgate.length > 0 && (
        <S.InfoContainer>
          <S.StatsRow>
            <S.StatCard>
              <S.StatIcon color={theme.colors.neutral}>
                <Icon icon="ph:calendar-blank-bold" height={18} />
              </S.StatIcon>
              <S.StatLabel>Pedido Realizado</S.StatLabel>
              <S.StatValue>{formatDate(props.DetalheResgate[0].DataOperacao)}</S.StatValue>
            </S.StatCard>

            <S.StatCard>
              <S.StatIcon color={theme.colors.primary}>
                <Icon icon="ph:currency-circle-dollar-bold" height={18} />
              </S.StatIcon>
              <S.StatLabel>Total</S.StatLabel>
              <S.StatValue style={{ color: theme.colors.primary }}>{formatToBRL(totalValorTransacao)}</S.StatValue>
            </S.StatCard>

            <S.StatCard>
              <S.StatIcon color={theme.colors.positive}>
                <Icon icon="ph:users-bold" height={18} />
              </S.StatIcon>
              <S.StatLabel>Pessoas Pagas</S.StatLabel>
              <S.StatValue>{props.DetalheResgate[0].QuantidadePessoasPagas}</S.StatValue>
            </S.StatCard>

            <S.StatCard>
              <S.StatIcon color={theme.colors.warning}>
                <Icon icon="ph:trophy-bold" height={18} />
              </S.StatIcon>
              <S.StatLabel>Premiações</S.StatLabel>
              <S.StatValue>{totalQuantidadePremiacoesPagas}</S.StatValue>
            </S.StatCard>
          </S.StatsRow>

          <S.Card>
            {Object.values(groupedData).map(function renderGroup({ avatar, usuarioNome, pedidos, pagos }) {
              return (
                <S.GroupSection key={avatar + usuarioNome}>
                  <S.GroupHeader>
                    <Avatar
                      src={avatar}
                      borderColor={theme.colors.primary}
                      size="sm"
                      alt="user avatar"
                      fallBack={StringUtil.GetFirstLetters(usuarioNome)}
                    />
                    <S.GroupName>{usuarioNome}</S.GroupName>
                  </S.GroupHeader>

                  {pedidos.length > 0 ? pedidos.map(function renderPedido(pedidoItem) {
                    return (
                      <S.ItemRow key={pedidoItem.CarteiraSaqueID}>
                        <S.ItemImage>
                          <img src={pedidoItem.Imagem} alt={pedidoItem.Nome} />
                        </S.ItemImage>
                        <S.ItemDetails>
                          <S.ItemName>{pedidoItem.Nome}</S.ItemName>
                          <S.ItemMeta>
                            <strong>{formatToBRL(pedidoItem.ValorOriginal)}</strong>
                            {pedidoItem.Qtde > 1 ? ` \u00d7 ${pedidoItem.Qtde} (${formatToBRL(pedidoItem.ValorUnitario)} un.)` : ''}
                          </S.ItemMeta>
                          <S.ItemMeta>
                            Taxas: <strong>{formatToBRL(Number(pedidoItem.ValorTotal) - Number(pedidoItem.ValorOriginal))}</strong>
                          </S.ItemMeta>
                        </S.ItemDetails>
                        {renderStatusTag(pedidoItem.status)}
                      </S.ItemRow>
                    );
                  }) : pagos.map(function renderPagos(pagosItem) {
                    return (
                      <S.ItemRow key={pagosItem.ID}>
                        <S.ItemDetails>
                          <S.ItemMeta>
                            Valor Pago: <strong style={{ fontSize: 16 }}>{formatToBRL(pagosItem.ValorPago)}</strong>
                          </S.ItemMeta>
                          <S.ItemMeta>
                            Chave Pix: <strong>{pagosItem.ChavePix}</strong>
                          </S.ItemMeta>
                          {pagosItem.Mensagem && (
                            <S.ItemMeta style={{ marginTop: 2 }}>{pagosItem.Mensagem}</S.ItemMeta>
                          )}
                        </S.ItemDetails>
                        {renderStatusTag(pagosItem.Status)}
                      </S.ItemRow>
                    );
                  })}

                  {pedidos.length > 0 && totalQuantidadePremiacoesPagas > 0 && (
                    <S.SubTotalRow>
                      <S.SubTotalLabel>Sub Total</S.SubTotalLabel>
                      <S.SubTotalValue>
                        {formatToBRL(pedidos.reduce(function sumTotal(total, item) { return total + Number(item.ValorTotal); }, 0))}
                      </S.SubTotalValue>
                    </S.SubTotalRow>
                  )}
                </S.GroupSection>
              );
            })}
          </S.Card>
        </S.InfoContainer>
      )}
    </S.Container>
  );
}

export default connector(DetalhePagamento);
